/**
 * Менеджер токенов для надежного управления авторизацией
 */
import { getCookie } from './cookieUtils';
import { refreshToken, clearTokens } from './authUtils';
import { TOKEN_CHECK_INTERVAL } from './constants';

class TokenManager {
    private checkInterval: NodeJS.Timeout | null = null;
    private isRefreshing = false;
    private refreshPromise: Promise<boolean> | null = null;

    /**
     * Запускает автоматическую проверку токенов
     */
    startTokenCheck(): void {
        if (this.checkInterval) {
            this.stopTokenCheck();
        }

        this.checkInterval = setInterval(() => {
            this.checkAndRefreshToken();
        }, TOKEN_CHECK_INTERVAL);
    }

    /**
     * Останавливает автоматическую проверку токенов
     */
    stopTokenCheck(): void {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }

    /**
     * Проверяет и обновляет токен при необходимости
     */
    async checkAndRefreshToken(): Promise<boolean> {
        // Если уже идет обновление, возвращаем существующий промис
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise;
        }

        const expiresAt = getCookie('token_expires_at');
        if (!expiresAt) {
            return false;
        }

        // Принудительно обновляем токен каждую минуту
        this.isRefreshing = true;
        this.refreshPromise = this.performTokenRefresh();

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    /**
     * Выполняет обновление токена
     */
    private async performTokenRefresh(): Promise<boolean> {
        try {
            const success = await refreshToken();
            if (success) {
                // Уведомляем о успешном обновлении токена
                window.dispatchEvent(new Event('token:refreshed'));
            }
            return success;
        } catch (error) {
            return false;
        }
    }

    /**
     * Принудительно обновляет токен
     */
    async forceRefreshToken(): Promise<boolean> {
        return this.performTokenRefresh();
    }

    /**
     * Проверяет валидность текущего токена
     */
    isTokenValid(): boolean {
        const expiresAt = getCookie('token_expires_at');
        if (!expiresAt) return false;

        const timeLeft = parseInt(expiresAt, 10) - Date.now();
        return timeLeft > 0;
    }

    /**
     * Получает время до истечения токена в минутах
     */
    getTimeUntilExpiration(): number {
        const expiresAt = getCookie('token_expires_at');
        if (!expiresAt) return 0;

        const timeLeft = parseInt(expiresAt, 10) - Date.now();
        return Math.max(0, Math.round(timeLeft / 1000 / 60));
    }

    /**
     * Очищает все токены и останавливает проверку
     */
    clearTokens(): void {
        this.stopTokenCheck();
        clearTokens();
    }
}

// Создаем единственный экземпляр менеджера токенов
export const tokenManager = new TokenManager();

// Экспортируем функции для удобства использования
export const startTokenCheck = () => tokenManager.startTokenCheck();
export const stopTokenCheck = () => tokenManager.stopTokenCheck();
export const checkAndRefreshToken = () => tokenManager.checkAndRefreshToken();
export const forceRefreshToken = () => tokenManager.forceRefreshToken();
export const isTokenValid = () => tokenManager.isTokenValid();
export const getTimeUntilExpiration = () => tokenManager.getTimeUntilExpiration();
